import { Box, Paper, Typography } from '@mui/material';
import { FC, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Table } from '../../../Components/Table/table.component';
import { BaseContentPage } from '../../../Components/Templates/BaseContentPage/base-content-page.componen';
import { BaseScreen } from '../../../Components/Templates/BaseScreen/basescreen.component';
import { Can } from '../../../Components/Can';
import { TableListSkeleton } from '../../../Components/Table/table-list-skeleton.component';
import { Placeholder } from '../../../Components/Placeholder/placeholder.component';
import { useListStocks } from './stocks-list.hook';
import { ButtonActionMenu } from '../../../Components/Form/ButtonActionsMenu/button-action-menu.component';
import { ListStocksUseCase } from '../../../Usecase/Stocks/list-stocks-usecase';
import { FaBoxOpen } from 'react-icons/fa';

interface IStocksList {
  listStocksUseCase: ListStocksUseCase
}

const QuantityCell: FC<{ value?: number | null }> = ({ value }) => (
  <Typography variant="body2" fontWeight={600} sx={{ width: '100%', textAlign: 'center' }}>
    {value ?? 0}
  </Typography>
);

const StocksList : FC<IStocksList> = ({ listStocksUseCase }) => {
    const navigate = useNavigate()

    const {
      list,
      totalRegisters,
      loading,
      loadError,
      page,
      currentSearch,
      handleSearch,
      handlePageChange,
      listStocks,
    } = useListStocks({
      listStocksUseCase,
    })

    useEffect(() => {
      listStocks({ page: 1, searchBy: '' })
    }, [listStocks])

    const columns = useMemo(() => {
        const createColumns: any[] = [
          {
            field: 'photo',
            headerName: 'Imagem',
            width: 100,
            sortable: false,
            renderCell: (params: any) => (
              <Box
                component="img"
                src={params.value}
                alt=""
                sx={{ width: '72px', height: '72px', objectFit: 'contain' }}
              />
            ),
          },
          {
            field: 'internalName',
            headerName: 'Nome Importado',
            flex: 1,
            minWidth: 160,
            sortable: false,
            renderCell: (params: any) => (
              <Box
                component={Link}
                to={`/stocks-details/${params.row.id}`}
                sx={{
                  color: 'inherit',
                  textDecoration: 'none',
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  lineHeight: 1.4,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {params.value || '—'}
              </Box>
            ),
          },
          {
            field: 'name',
            headerName: 'Nome Amigável',
            flex: 1,
            minWidth: 160,
            sortable: false,
            renderCell: (params: any) => (
              <Typography
                variant="body2"
                sx={{ whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.4 }}
              >
                {params.value || '—'}
              </Typography>
            ),
          },
          {
            field: 'allocatedStockQuantity',
            headerName: 'Empenho',
            width: 110,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => <QuantityCell value={params.value} />,
          },
          {
            field: 'workInProgressQuantity',
            headerName: 'Produção',
            width: 110,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => <QuantityCell value={params.value} />,
          },
          {
            field: 'finishedGoodsQuantity',
            headerName: 'Acabado',
            width: 110,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => <QuantityCell value={params.value} />,
          },
          {
            field: "actions",
            headerName: "Ações",
            width: 80,
            sortable: false,
            renderCell: (params: any) => (
              <ButtonActionMenu
                options={[
                  {
                    title: 'Ver estoque',
                    onClick: () => navigate(`/stocks-details/${params.row.id}`),
                    icon: <FaBoxOpen size={18} />,
                  },
                ]}
              />
            ),
          },
        ];

        return createColumns;
      }, [navigate]);

    return (
      <Can permissions={['menu:stocks:manage']}>
        <BaseScreen>
            <Paper sx={{ borderRadius: 2, overflow: 'hidden', width: '100%' }}>
                <BaseContentPage
                    title="Estoque"
                    subtitle="Acompanhe empenho, produção e acabado por produto"
                    minHeight="auto"
                    search
                    searchDefaultValue={currentSearch}
                    searchFunction={handleSearch}
                    searchLoading={loading && !!list?.length}
                    placeholderSearch="Pesquisar por nome ou código"
                    breadcrumb={[
                      { title: 'Estoque', link: "/stocks-list" },
                      { title: 'Listagem' },
                    ]}
                >
                  {!!list?.length ? (
                    <Box sx={{ width: '100%', minWidth: 0 }}>
                      <Table
                        data={list}
                        columns={columns}
                        totalCount={totalRegisters}
                        search={currentSearch}
                        page={page}
                        onPageChange={handlePageChange}
                        rowHeight={88}
                        stickyHeader
                        maxHeight="calc(100vh - 280px)"
                        sx={{ width: '100%' }}
                      />
                    </Box>
                  ) : loading ? (
                    <TableListSkeleton rowHeight={88} />
                  ) : loadError ? (
                    <Placeholder
                      description="Não foi possível carregar o estoque."
                      onRetry={() => listStocks({ page, searchBy: currentSearch })}
                    />
                  ) : (
                    <Placeholder description="Nenhum produto com estoque encontrado." />
                  )}
                </BaseContentPage>
            </Paper>
        </BaseScreen>
        </Can>
    )
}

export { StocksList };
